// Recursive WHERE boolean-tree parser (Slice 3B.1). Parses a WHERE byte range
// into a `BooleanNode` tree — the 3B source of truth. Homogeneous groups: a
// group's children share one connector, and mixed top-level AND/OR is
// normalised into nested groups by SQL precedence (NOT > AND > OR). Pure
// functions; reuses the shared scan primitives so identifier/string/comment
// skipping stays engine-correct.
//
// Spec: goals/vqe-improvements/design.md § Sub-slice 3B (D11).

import { rangeFromOffsets } from '../../sql-grammar/document/lineIndex';
import type { BooleanFragment, BooleanNode } from '../types';
import { buildBooleanFragment, trimPredicateBoundary } from './extractQueryStructure.predicates';

import { readIdentifier } from '../scope/identifiers';
import type { SelectExtractCtx } from './extractQueryStructure.select';
import { parseComparisonFragment } from './parseComparison';
import { skipContextFor } from '../classify/tokenSkip';
import { scanTopLevelKeywords, skipParen } from './scanner';

// Top entry: parse [start, end) into a tree whose root is always a `group`
// (degenerate single-child when the clause is one predicate). Undefined for an
// empty / whitespace-only range (mirrors parseBooleanFragments).
export function parseBooleanTree(
  ctx: SelectExtractCtx,
  start: number,
  end: number,
): BooleanNode | undefined {
  const node = parseRun(ctx, start, end);
  if (!node) return undefined;
  if (node.kind === 'group') return node;
  return {
    kind: 'group', connector: 'AND', negated: false, explicit: false,
    children: [node], range: node.range,
  };
}

// Parse a run of predicates joined by top-level AND/OR into one node, applying
// SQL precedence (OR splits first). Returns a leaf for a single predicate.
function parseRun(ctx: SelectExtractCtx, start: number, end: number): BooleanNode | undefined {
  const { source, options } = ctx;
  const trimmed = trimPredicateBoundary(source, start, end, options);
  if (trimmed.startOffset >= trimmed.endOffset) return undefined;
  const kws = scanTopLevelKeywords(source, trimmed.startOffset, trimmed.endOffset, options);
  const spans: { start: number; end: number }[] = [];
  const connectors: ('AND' | 'OR')[] = [];
  let pendingBetween = false;
  let s = trimmed.startOffset;
  for (const k of kws) {
    if (k.upper === 'BETWEEN') { pendingBetween = true; continue; }
    if (k.upper === 'AND' && pendingBetween) { pendingBetween = false; continue; }
    if (k.upper !== 'AND' && k.upper !== 'OR') continue;
    spans.push({ start: s, end: k.start });
    connectors.push(k.upper);
    s = k.end;
  }
  spans.push({ start: s, end: trimmed.endOffset });
  const elements = spans.map((sp) => parseElement(ctx, sp.start, sp.end));
  return normalizePrecedence(ctx, elements, connectors);
}

const isWs = (c: string): boolean => c === ' ' || c === '\t' || c === '\n' || c === '\r';

// Returns the index just past a leading `NOT` keyword at `i`, else -1.
function matchLeadingNot(ctx: SelectExtractCtx, i: number, end: number): number {
  const id = readIdentifier(ctx.source, i, end, ctx.options);
  if (id && !id.quoted && id.value.toUpperCase() === 'NOT') return id.end;
  return -1;
}

// Parse one element span: an optional leading `NOT` before a wholly-
// parenthesised `(…)` group (→ negated explicit group, range includes NOT),
// a plain `(…)` group, or a bare leaf. A bare `NOT <pred>` stays opaque leaf text.
function parseElement(ctx: SelectExtractCtx, start: number, end: number): BooleanNode {
  const { source, options, lineIdx } = ctx;
  const trimmed = trimPredicateBoundary(source, start, end, options);
  let i = trimmed.startOffset;
  let negated = false;
  const afterNot = matchLeadingNot(ctx, i, trimmed.endOffset);
  if (afterNot >= 0) {
    let j = afterNot;
    while (j < trimmed.endOffset && isWs(source[j])) j++;
    if (source[j] === '(') { negated = true; i = j; }
  }
  if (source[i] === '(') {
    const close = skipParen(source, i, trimmed.endOffset, skipContextFor(options)); // index past ')'
    const tail = trimPredicateBoundary(source, close, trimmed.endOffset, options);
    if (tail.startOffset >= tail.endOffset) {
      const inner = parseRun(ctx, i + 1, close - 1);
      const range = rangeFromOffsets(lineIdx, trimmed.startOffset, trimmed.endOffset);
      if (inner && inner.kind === 'group') {
        return { kind: 'group', connector: inner.connector, negated, explicit: true, children: inner.children, range };
      }
      return { kind: 'group', connector: 'AND', negated, explicit: true, children: inner ? [inner] : [], range };
    }
  }
  return makeLeaf(ctx, trimmed.startOffset, trimmed.endOffset);
}

function makeLeaf(ctx: SelectExtractCtx, start: number, end: number): BooleanNode {
  const { source, options, lineIdx } = ctx;
  const t = trimPredicateBoundary(source, start, end, options);
  const comparison = parseComparisonFragment(source, t.startOffset, t.endOffset, options, lineIdx);
  return {
    kind: 'leaf',
    range: rangeFromOffsets(lineIdx, t.startOffset, t.endOffset),
    text: source.slice(t.startOffset, t.endOffset),
    negated: false,
    ...(comparison ? { comparison } : {}),
  };
}

// Combine elements by SQL precedence: OR (lowest) splits into segments, each an
// AND-run; a single element stays a leaf/group (no implicit wrapper).
function normalizePrecedence(
  ctx: SelectExtractCtx,
  elements: readonly BooleanNode[],
  connectors: readonly ('AND' | 'OR')[],
): BooleanNode {
  if (elements.length === 1) return elements[0];
  const orSegments: BooleanNode[][] = [[]];
  let hasOr = false;
  elements.forEach((el, idx) => {
    if (idx > 0 && connectors[idx - 1] === 'OR') { orSegments.push([]); hasOr = true; }
    orSegments[orSegments.length - 1].push(el);
  });
  if (hasOr) return implicitGroup(ctx, 'OR', orSegments.map((seg) => andNode(ctx, seg)));
  return implicitGroup(ctx, 'AND', elements);
}

function andNode(ctx: SelectExtractCtx, seg: readonly BooleanNode[]): BooleanNode {
  return seg.length === 1 ? seg[0] : implicitGroup(ctx, 'AND', seg);
}

function implicitGroup(
  ctx: SelectExtractCtx,
  connector: 'AND' | 'OR',
  children: readonly BooleanNode[],
): BooleanNode {
  const first = children[0];
  const last = children[children.length - 1];
  return {
    kind: 'group', connector, negated: false, explicit: false, children,
    range: rangeFromOffsets(ctx.lineIdx, first.range.startOffset, last.range.endOffset),
  };
}

// Derive the flat BooleanFragment[] view from the tree, byte-identical to
// parseBooleanFragments. Implicit (precedence) groups are flattened
// transparently — threading the group's connector to its non-first children —
// while leaves and explicit `(…)` groups stay opaque fragments. A wholly-
// parenthesised clause (the root itself is explicit) is one opaque fragment.
export function flattenBooleanTree(
  ctx: SelectExtractCtx,
  root: BooleanNode,
): readonly BooleanFragment[] | undefined {
  const { source, options, lineIdx } = ctx;
  const out: BooleanFragment[] = [];
  const emit = (node: BooleanNode, leadingConnector: 'AND' | 'OR' | undefined): void => {
    if (node.kind === 'group' && !node.explicit) {
      node.children.forEach((c, idx) => emit(c, idx === 0 ? leadingConnector : node.connector));
      return;
    }
    const t = trimPredicateBoundary(source, node.range.startOffset, node.range.endOffset, options);
    if (t.startOffset >= t.endOffset) return;
    out.push({
      ...buildBooleanFragment(source, options, lineIdx, t.startOffset, t.endOffset),
      ...(leadingConnector ? { leadingConnector } : {}),
    });
  };
  if (root.kind === 'group' && !root.explicit) {
    root.children.forEach((c, idx) => emit(c, idx === 0 ? undefined : root.connector));
  } else {
    emit(root, undefined);
  }
  return out.length ? out : undefined;
}
